/**
 * ChatKart Bridge Token UI (Apr 26, 2026)
 *
 * Renders inside Settings -> Integrations. Admins can mint a long-lived
 * Personal Access Token to paste into ChatKart's Add-Workspace wizard.
 * Plaintext is shown exactly once at mint time. Existing tokens are
 * listed by last 4 chars + create/expire dates with a Revoke button.
 *
 * Hidden entirely for non-admin users — caller is expected to gate this
 * via the existing `isAdmin` check on Settings -> Integrations tab, but
 * we double-check here defensively.
 */

import { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import {
  Loader2,
  KeyRound,
  Copy,
  Check,
  Trash2,
  AlertTriangle,
  Plus,
  X,
} from 'lucide-react';

interface BridgeTokenRow {
  id: string;
  tokenLast4: string;
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  createdBy: string | null;
  isExpired: boolean;
}

interface MintResponse {
  token: string;
  workspaceId: string;
  tenantSlug: string;
  displayName: string;
  expiresAt: string;
  id: string;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function ChatKartBridgeCard() {
  const { user } = useAuthStore();
  const isAdmin = user?.role?.name === 'admin';

  const [tokens, setTokens] = useState<BridgeTokenRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [minting, setMinting] = useState(false);
  const [revealedToken, setRevealedToken] = useState<MintResponse | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    void loadTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const loadTokens = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/integrations/chatkart/bridge-tokens');
      setTokens(res.data.data ?? []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load tokens');
    } finally {
      setLoading(false);
    }
  };

  const handleMint = async () => {
    if (
      !window.confirm(
        'Generate a new ChatKart bridge token? The plaintext will be shown only once — store it safely before closing the dialog.',
      )
    ) {
      return;
    }
    setMinting(true);
    setError(null);
    try {
      const res = await api.post('/integrations/chatkart/bridge-tokens', {});
      setRevealedToken(res.data as MintResponse);
      await loadTokens();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to mint token');
    } finally {
      setMinting(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (
      !window.confirm(
        'Revoke this token? Any ChatKart workspace currently paired with it will stop working.',
      )
    ) {
      return;
    }
    try {
      await api.delete(`/integrations/chatkart/bridge-tokens/${id}`);
      await loadTokens();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to revoke token');
    }
  };

  const handleCopy = async () => {
    if (!revealedToken?.token) return;
    try {
      await navigator.clipboard.writeText(revealedToken.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback — manual copy
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--saffron-50)] text-[var(--ink-0)]">
            <KeyRound size={18} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              ChatKart Bridge
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Use this token in ChatKart&rsquo;s Add-Workspace wizard to pair
              this AURA workspace as your CRM layer. Tokens are scoped to this
              tenant and never expire unless revoked or aged out (default
              365&nbsp;days).
            </p>
          </div>
        </div>
        <button
          onClick={handleMint}
          disabled={minting}
          className="flex shrink-0 items-center gap-2 rounded-lg bg-[var(--ink-0)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
        >
          {minting ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Plus size={14} />
          )}
          Generate token
        </button>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* One-shot reveal */}
      {revealedToken && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <AlertTriangle size={14} />
              This is the only time you&rsquo;ll see the full token. Store it
              safely.
            </div>
            <button
              onClick={() => setRevealedToken(null)}
              className="rounded p-1 text-amber-900 hover:bg-amber-100"
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded border border-amber-200 bg-white px-3 py-2 font-mono text-xs text-gray-900">
              {revealedToken.token}
            </code>
            <button
              onClick={handleCopy}
              className="flex shrink-0 items-center gap-1 rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              {copied ? (
                <>
                  <Check size={12} /> Copied
                </>
              ) : (
                <>
                  <Copy size={12} /> Copy
                </>
              )}
            </button>
          </div>
          <div className="mt-2 text-xs text-amber-800">
            Workspace:{' '}
            <span className="font-semibold">{revealedToken.displayName}</span>{' '}
            ({revealedToken.tenantSlug}) &middot; expires{' '}
            {formatDate(revealedToken.expiresAt)}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 size={20} className="animate-spin text-gray-400" />
        </div>
      ) : tokens.length === 0 ? (
        <p className="py-4 text-center text-sm text-gray-400">
          No active tokens yet.
        </p>
      ) : (
        <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
          {tokens.map((t) => (
            <div
              key={t.id}
              className="flex items-center justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                    aura_pat_&middot;&middot;&middot;&middot; &middot;&middot;&middot;&middot;
                    {t.tokenLast4}
                  </code>
                  {t.isExpired && (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-700">
                      Expired
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  Created {formatDate(t.createdAt)}
                  {t.createdBy && <> by {t.createdBy}</>} &middot; expires{' '}
                  {formatDate(t.expiresAt)}
                  {t.lastUsedAt && (
                    <> &middot; last used {formatDate(t.lastUsedAt)}</>
                  )}
                </div>
              </div>
              <button
                onClick={() => handleRevoke(t.id)}
                className="flex shrink-0 items-center gap-1 rounded-lg border border-red-200 bg-white px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
              >
                <Trash2 size={12} />
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
